/**
 * GameChat - In-game chat UI for Midgard
 * 
 * Features:
 * - Local chat (scene-wide)
 * - Zone chat (when seated)
 * - Direct messages (Talk)
 * - Voice chat (Direct Talk and Zone Voice)
 * - Desktop: keyboard shortcuts, Enter to send
 * - Mobile: touch-friendly, no interference with game controls
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, Send, X, Users, User, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Chat mode enum
 */
export const ChatMode = {
  LOCAL: 'local',     // Scene-wide chat
  ZONE: 'zone',       // Social zone chat (when seated)
  DIRECT: 'direct',   // Direct message / Talk
};

/**
 * Format timestamp
 */
function formatTime(timestamp) {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Single chat message
 */
function ChatMessage({ message, isOwn }) {
  const typeLabel = {
    local: '',
    zone: '🪑',
    direct: '💬',
  }[message.chatType] || '';
  
  return (
    <div className={`flex gap-2 py-1 ${isOwn ? 'justify-end' : ''}`}>
      <div className={`max-w-[85%] ${isOwn ? 'order-2' : ''}`}>
        <div className={`flex items-center gap-1 ${isOwn ? 'justify-end' : ''}`}>
          {!isOwn && (
            <span className="text-xs font-medium text-amber-400">
              {message.senderName}
            </span>
          )}
          {typeLabel && <span className="text-xs">{typeLabel}</span>}
          <span className="text-xs text-white/30">{formatTime(message.timestamp)}</span>
        </div>
        <div className={`px-3 py-1.5 rounded-lg text-sm ${
          isOwn 
            ? 'bg-amber-600/60 text-white' 
            : 'bg-black/40 text-white/90'
        }`}>
          {message.content}
        </div>
      </div>
    </div>
  );
}

/**
 * Chat input field
 */
function ChatInput({ onSend, placeholder, disabled, onFocusChange }) {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  
  const handleSend = useCallback(() => {
    if (text.trim() && !disabled) {
      onSend(text.trim());
      setText('');
    }
  }, [text, onSend, disabled]);
  
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Prevent WASD from moving player while typing
    e.stopPropagation();
  }, [handleSend]);
  
  // Also stop keyUp events from reaching the game
  const handleKeyUp = useCallback((e) => {
    e.stopPropagation();
  }, []);
  
  return (
    <div className="flex gap-2" onKeyDown={(e) => e.stopPropagation()} onKeyUp={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={() => onFocusChange?.(true)}
        onBlur={() => onFocusChange?.(false)}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 px-3 py-2 rounded-lg bg-black/60 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-amber-500/50"
        maxLength={500}
        data-testid="chat-input"
      />
      <button
        onClick={handleSend}
        disabled={!text.trim() || disabled}
        className="p-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid="chat-send-btn"
      >
        <Send className="w-4 h-4 text-white" />
      </button>
    </div>
  );
}

/**
 * Main Game Chat component
 */
export default function GameChat({
  messages = [],
  localMessages = [],
  zoneMessages = [],
  directMessages = [],
  onSendLocal,
  onSendZone,
  onSendDirect,
  currentPlayerId,
  isSeated,
  currentZoneId,
  currentZoneLabel,
  directTarget,
  onCloseDirectChat,
  isMobile = false,
  onChatFocusChange,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState(ChatMode.LOCAL);
  const [hasUnread, setHasUnread] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Notify parent of focus changes
  useEffect(() => {
    onChatFocusChange?.(isFocused);
  }, [isFocused, onChatFocusChange]);
  
  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (isOpen && !isMinimized && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [localMessages, zoneMessages, directMessages, isOpen, isMinimized]);
  
  // Track unread messages when chat is closed
  useEffect(() => {
    if (!isOpen) {
      setHasUnread(true);
    }
  }, [localMessages.length, zoneMessages.length, directMessages.length, isOpen]);
  
  // Switch to zone tab when seated
  useEffect(() => {
    if (isSeated && currentZoneId) {
      setActiveTab(ChatMode.ZONE);
    }
  }, [isSeated, currentZoneId]);
  
  // Switch to direct tab when direct target is set
  useEffect(() => {
    if (directTarget) {
      setActiveTab(ChatMode.DIRECT);
      setIsOpen(true);
      setIsMinimized(false);
    }
  }, [directTarget]);
  
  const handleToggle = () => {
    if (!isOpen) {
      setHasUnread(false);
    }
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };
  
  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };
  
  const getCurrentMessages = () => {
    switch (activeTab) {
      case ChatMode.ZONE:
        return zoneMessages;
      case ChatMode.DIRECT:
        return directMessages;
      default:
        return localMessages;
    }
  };
  
  const handleSend = (text) => {
    switch (activeTab) {
      case ChatMode.ZONE:
        if (onSendZone) onSendZone(text);
        break;
      case ChatMode.DIRECT:
        if (onSendDirect && directTarget) onSendDirect(directTarget.playerId, text);
        break;
      default:
        if (onSendLocal) onSendLocal(text);
    }
  };
  
  const getPlaceholder = () => {
    switch (activeTab) {
      case ChatMode.ZONE:
        return `Message ${currentZoneLabel || 'zone'}...`;
      case ChatMode.DIRECT:
        return `Message ${directTarget?.displayName || 'player'}...`;
      default:
        return 'Chat with nearby players...';
    }
  };
  
  // Chat button (when closed)
  if (!isOpen) {
    return (
      <button
        onClick={handleToggle}
        className={`fixed z-40 p-3 rounded-full bg-black/70 border border-amber-900/40 hover:border-amber-500/50 transition-all ${hasUnread ? 'animate-pulse' : ''}`}
        style={{
          // The bottom-right corner belongs to the ecctrl action buttons (+ the
          // contextual Interact button above them) and the lifted PTT column.
          // Park the toggle on the left, just above the ecctrl joystick.
          bottom: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 180px)' : '16px',
          left: isMobile ? 'calc(env(safe-area-inset-left, 0px) + 16px)' : 'auto',
          right: isMobile ? 'auto' : '16px',
        }}
        data-testid="chat-toggle-btn"
      >
        <MessageSquare className={`w-5 h-5 ${hasUnread ? 'text-amber-400' : 'text-white/70'}`} />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full" />
        )}
      </button>
    );
  }
  
  // Chat panel
  return (
    <div 
      className={`fixed z-40 flex flex-col bg-black/80 backdrop-blur-md rounded-xl border border-amber-900/40 overflow-hidden`}
      style={{ 
        maxHeight: isMobile ? '60vh' : '400px',
        bottom: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 80px)' : '16px',
        right: isMobile ? '8px' : '16px',
        left: isMobile ? '8px' : 'auto',
        width: isMobile ? 'auto' : '320px',
      }}
      data-testid="chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-black/40 border-b border-amber-900/30">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-medium text-amber-100">Chat</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleMinimize}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            {isMinimized ? (
              <ChevronUp className="w-4 h-4 text-white/60" />
            ) : (
              <ChevronDown className="w-4 h-4 text-white/60" />
            )}
          </button>
          <button
            onClick={handleToggle}
            className="p-1 hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-4 h-4 text-white/60" />
          </button>
        </div>
      </div>
      
      {!isMinimized && (
        <>
          {/* Tabs */}
          <div className="flex border-b border-amber-900/30">
            <button
              onClick={() => setActiveTab(ChatMode.LOCAL)}
              className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                activeTab === ChatMode.LOCAL 
                  ? 'text-amber-400 border-b-2 border-amber-400' 
                  : 'text-white/50 hover:text-white/70'
              }`}
            >
              <Users className="w-3 h-3" />
              Local
            </button>
            
            {isSeated && currentZoneId && (
              <button
                onClick={() => setActiveTab(ChatMode.ZONE)}
                className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                  activeTab === ChatMode.ZONE 
                    ? 'text-amber-400 border-b-2 border-amber-400' 
                    : 'text-white/50 hover:text-white/70'
                }`}
              >
                🪑 {currentZoneLabel || 'Zone'}
              </button>
            )}
            
            {directTarget && (
              <button
                onClick={() => setActiveTab(ChatMode.DIRECT)}
                className={`flex-1 px-3 py-2 text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                  activeTab === ChatMode.DIRECT 
                    ? 'text-amber-400 border-b-2 border-amber-400' 
                    : 'text-white/50 hover:text-white/70'
                }`}
              >
                <User className="w-3 h-3" />
                {directTarget.displayName}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onCloseDirectChat) onCloseDirectChat();
                  }}
                  className="ml-1 p-0.5 hover:bg-white/20 rounded"
                >
                  <X className="w-3 h-3" />
                </button>
              </button>
            )}
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-1 min-h-[150px] max-h-[250px]">
            {getCurrentMessages().length === 0 ? (
              <div className="text-center text-white/30 text-sm py-4">
                {activeTab === ChatMode.ZONE 
                  ? `Start a conversation at ${currentZoneLabel || 'this table'}` 
                  : activeTab === ChatMode.DIRECT
                  ? `Start talking to ${directTarget?.displayName}`
                  : 'No messages yet. Say hello!'}
              </div>
            ) : (
              getCurrentMessages().map((msg) => (
                <ChatMessage 
                  key={msg.id} 
                  message={msg} 
                  isOwn={msg.senderId === currentPlayerId}
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input */}
          <div className="p-3 border-t border-amber-900/30">
            <ChatInput
              onSend={handleSend}
              placeholder={getPlaceholder()}
              disabled={activeTab === ChatMode.ZONE && !isSeated}
              onFocusChange={setIsFocused}
            />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Mobile chat button with context indicator
 */
export function MobileChatButton({ 
  hasUnread, 
  onClick, 
  isSeated,
  directChatActive,
}) {
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-full bg-black/70 border border-amber-900/40 ${hasUnread ? 'animate-pulse border-amber-500' : ''}`}
      data-testid="mobile-chat-btn"
    >
      <MessageSquare className={`w-5 h-5 ${hasUnread ? 'text-amber-400' : 'text-white/70'}`} />
      {hasUnread && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full" />
      )}
    </button>
  );
}
